// ADO-2262: Collapse, the expand/collapse motion from the ShopLink 2.0 prototype (its
// MOTION.md `Collapse`): the height runs between `grid-template-rows: 0fr` and `1fr` (never
// `height: auto`) on `--motion-base` and `--ease-standard`, and by default the content fades
// in just after it starts to open and out as soon as it starts to close.
//
// Presentational: the host owns `open` and its toggle's `aria-expanded` / `aria-controls`
// (pass the same `id`), which change on the user's action, not when the motion ends.
//
// Closed content stays mounted, so the toggle's `aria-controls` always names it and the close
// can be seen. It is `inert` and `aria-hidden` from the moment it starts to close (no focus, no
// pointer, out of the accessibility tree), and `visibility: hidden` once the rows have finished
// collapsing (on their `transitionend`, or after the length and a grace, should the browser
// never report it). While it moves the content is clipped to the box; at rest open it may draw
// past it (a focus ring, a pulled-out panel). Under reduced motion it opens and closes at once.
//
// `appear` grows a Collapse mounted open in from closed (a host that mounts it only while
// shown); `onClosed` tells such a host that the close has finished, so it can unmount.
// `className` places the box in its host's layout.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { EXIT_GRACE_MS, MOTION_MS, prefersReducedMotion } from '../motion';
import styles from './Collapse.module.css';

export interface CollapseProps {
  readonly open: boolean;
  /** DOM id of the box, for the toggle's `aria-controls`. */
  readonly id?: string | undefined;
  /** Fade the content with the height (default). Off where only the height should move. */
  readonly fade?: boolean;
  /** Mounted open, grow in from closed rather than appearing at full height. */
  readonly appear?: boolean;
  /** Extra class for the box, to place it in its host's layout. */
  readonly className?: string | undefined;
  /** Called once a close has finished. */
  readonly onClosed?: (() => void) | undefined;
  readonly children: ReactNode;
}

/** `moving` while the rows run to `open`; `appearing` while a Collapse mounted open grows in. */
type Phase = 'settled' | 'moving' | 'appearing';

const CLOSED_AT_REST: CSSProperties = { visibility: 'hidden' };

export function Collapse({
  open,
  id,
  fade = true,
  appear = false,
  className,
  onClosed,
  children,
}: CollapseProps) {
  const [motion, setMotion] = useState<{ open: boolean; phase: Phase }>(() => ({
    open,
    phase: appear && open && !prefersReducedMotion() ? 'appearing' : 'settled',
  }));
  if (motion.open !== open) {
    setMotion({ open, phase: prefersReducedMotion() ? 'settled' : 'moving' });
  }

  const settle = useCallback(
    () =>
      setMotion((current) =>
        current.phase === 'settled' ? current : { ...current, phase: 'settled' },
      ),
    [],
  );

  // The fallback for rows whose `transitionend` (or `animationend`) never arrives.
  useEffect(() => {
    if (motion.phase === 'settled') return;
    const timer = setTimeout(settle, MOTION_MS.base + EXIT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [motion, settle]);

  // Read through a ref so a host passing an inline `onClosed` does not re-run the effect.
  const onClosedRef = useRef(onClosed);
  useEffect(() => {
    onClosedRef.current = onClosed;
  }, [onClosed]);
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (motion.open) {
      wasOpenRef.current = true;
    } else if (motion.phase === 'settled' && wasOpenRef.current) {
      wasOpenRef.current = false;
      onClosedRef.current?.();
    }
  }, [motion]);

  const closedAtRest = !open && motion.phase === 'settled';
  return (
    <div
      id={id}
      className={className === undefined ? styles.collapse : `${styles.collapse} ${className}`}
      data-open={open ? '' : undefined}
      data-settled={motion.phase === 'settled' ? '' : undefined}
      data-appear={motion.phase === 'appearing' ? '' : undefined}
      data-fade={fade ? '' : undefined}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && event.propertyName === 'grid-template-rows') {
          settle();
        }
      }}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) settle();
      }}
    >
      <div
        className={styles.content}
        inert={!open}
        aria-hidden={open ? undefined : true}
        style={closedAtRest ? CLOSED_AT_REST : undefined}
      >
        {children}
      </div>
    </div>
  );
}
